import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, ExternalLink, ReceiptText, Wallet } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUserCreditWallet } from '@/hooks/useUsers';
import { buildAxiomBillingUrl } from '@/lib/axiom';
import { userEditSearchSchema } from '@/lib/route-search';
import type { CreditLedgerEntry, CreditWalletSummary } from '@/types/users';

import type { MultiValue } from 'react-select';

const PAGE_SIZE = 100;

const LEDGER_SOURCES = [
  'purchase',
  'debit',
  'expiry',
  'admin',
  'team_auto_draw',
] as const;

const LEDGER_SOURCE_OPTIONS = LEDGER_SOURCES.map((source) => ({
  value: source,
  label: source.replace(/_/g, ' '),
}));

type LedgerSourceOption = (typeof LEDGER_SOURCE_OPTIONS)[number];

const LEDGER_FEATURES = [
  'chat',
  'routine_run',
  'routine_run_lite',
  'routine_run_max',
  'routine_run_ultra',
  'meeting_prep',
  'meeting_assist',
  'journal',
  'external_user_context_search',
  'voice',
] as const;

const LEDGER_FEATURE_OPTIONS = LEDGER_FEATURES.map((feature) => ({
  value: feature,
  label: feature.replace(/_/g, ' '),
}));

type LedgerFeatureOption = (typeof LEDGER_FEATURE_OPTIONS)[number];

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

interface UserCreditWalletSectionProps {
  userId: number;
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUsd(value: number | null): string {
  return value == null ? '-' : `$${value.toFixed(4)}`;
}

function formatDelta(delta: number): string {
  return `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`;
}

function sourceClassName(source: string): string {
  if (source === 'debit') return 'bg-red-50 text-red-700 border-red-200';
  if (source === 'purchase') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (source.endsWith('_grant')) {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }
  if (source === 'expiry') {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  return 'bg-muted text-muted-foreground border-border';
}

function sourceLabel(source: string): string {
  return source.replace(/_/g, ' ');
}

function walletTitle(wallet: CreditWalletSummary): string {
  return wallet.wallet_kind === 'team' ? 'Team Wallet' : 'Personal Wallet';
}

function WalletSummaryCard({ wallet }: { wallet: CreditWalletSummary }) {
  return (
    <div className="border rounded-md p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{walletTitle(wallet)}</p>
          <p className="text-xs text-muted-foreground">
            {wallet.id} / {wallet.plan_tier}
          </p>
        </div>
        <Badge variant="outline">{wallet.total_balance.toLocaleString()}</Badge>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        <div>
          <p className="text-xs text-muted-foreground">Daily</p>
          <p className="text-lg font-semibold">
            {wallet.daily_balance.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Plan</p>
          <p className="text-lg font-semibold">
            {wallet.plan_balance.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Purchased</p>
          <p className="text-lg font-semibold">
            {wallet.purchased_balance.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Monthly Spend</p>
          <p className="text-lg font-semibold">
            {wallet.monthly_spend.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 md:grid-cols-4">
        <div>
          <p className="text-xs text-muted-foreground">Monthly Grant</p>
          <p>{wallet.plan_grant_amount.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Daily Grant</p>
          <p>{wallet.daily_grant_amount.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Cycle Start</p>
          <p>{formatDateTime(wallet.cycle_start)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Auto-Refill</p>
          <p>{wallet.auto_refill_enabled ? 'Enabled' : 'Off'}</p>
        </div>
      </div>

      {wallet.auto_refill_last_failure_reason && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          {wallet.auto_refill_last_failure_reason}
        </div>
      )}
    </div>
  );
}

function usedPools(entry: CreditLedgerEntry): string {
  const parts = [
    ['daily', entry.daily_credits_used],
    ['plan', entry.plan_credits_used],
    ['purchased', entry.purchased_credits_used],
  ]
    .filter(([, value]) => typeof value === 'number' && value > 0)
    .map(([label, value]) => `${label}: ${Number(value).toLocaleString()}`);
  if (entry.overdraft && entry.overdraft > 0) {
    parts.push(`overdraft: ${entry.overdraft.toLocaleString()}`);
  }
  return parts.length > 0 ? parts.join(', ') : '-';
}

function LedgerTable({
  title,
  wallet,
  userId,
}: {
  title: string;
  wallet: CreditWalletSummary;
  userId: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          {title}
        </p>
        <p className="text-xs text-muted-foreground">
          {wallet.ledger_total_count.toLocaleString()} entries
        </p>
      </div>
      <div className="border rounded-md">
        <Table
          containerClassName="max-h-96 overflow-auto"
          className="min-w-[820px]"
        >
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="h-9 px-3 text-xs">Time</TableHead>
              <TableHead className="h-9 px-3 text-xs">Source</TableHead>
              <TableHead className="h-9 px-3 text-xs">Feature</TableHead>
              <TableHead className="h-9 px-3 text-xs text-right">
                Delta
              </TableHead>
              <TableHead className="h-9 px-3 text-xs">Spend</TableHead>
              <TableHead className="h-9 px-3 text-xs">Reference</TableHead>
              <TableHead className="h-9 px-3 text-xs">Trace</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {wallet.ledger_entries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-6 text-center text-sm text-muted-foreground"
                >
                  No ledger entries
                </TableCell>
              </TableRow>
            ) : (
              wallet.ledger_entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  className={
                    entry.overdraft && entry.overdraft > 0 ? 'bg-red-50' : ''
                  }
                >
                  <TableCell className="py-3 px-3 whitespace-nowrap">
                    {formatDateTime(entry.created_at)}
                  </TableCell>
                  <TableCell className="py-3 px-3">
                    <Badge
                      variant="outline"
                      className={sourceClassName(entry.source)}
                    >
                      {sourceLabel(entry.source)}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-3 px-3">
                    {entry.feature ?? '-'}
                  </TableCell>
                  <TableCell
                    className={`py-3 px-3 text-right font-semibold ${
                      entry.delta < 0 ? 'text-red-700' : 'text-emerald-700'
                    }`}
                  >
                    {formatDelta(entry.delta)}
                  </TableCell>
                  <TableCell className="py-3 px-3 min-w-56">
                    <div className="space-y-1 text-xs">
                      <p className="text-sm">
                        {entry.actual_cost_usd == null
                          ? usedPools(entry)
                          : `${formatUsd(entry.actual_cost_usd)} -> ${
                              entry.computed_credits?.toLocaleString() ?? '-'
                            } credits`}
                      </p>
                      {entry.actual_cost_usd != null && (
                        <p className="text-muted-foreground">
                          {usedPools(entry)}
                        </p>
                      )}
                      {(entry.model_name || entry.agent) && (
                        <p className="text-muted-foreground">
                          {[entry.model_name, entry.agent]
                            .filter(Boolean)
                            .join(' / ')}
                        </p>
                      )}
                      {entry.total_tokens != null && (
                        <p className="text-muted-foreground">
                          {entry.total_tokens.toLocaleString()} tokens
                        </p>
                      )}
                      {entry.overdraft != null && entry.overdraft > 0 && (
                        <Badge
                          variant="outline"
                          className="bg-red-50 text-red-700 border-red-200"
                        >
                          <AlertTriangle className="mr-1 h-3 w-3" />
                          overdraft {entry.overdraft.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 px-3 max-w-72">
                    <code className="block truncate text-xs">
                      {entry.reference_id ?? '-'}
                    </code>
                    {(entry.grant_period || entry.expires_at) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {entry.grant_period
                          ? `period ${entry.grant_period}`
                          : null}
                        {entry.expires_at
                          ? ` expires ${formatDateTime(entry.expires_at)}`
                          : null}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="py-3 px-3">
                    {entry.reference_id ? (
                      <a
                        href={buildAxiomBillingUrl({
                          // Team-wallet entries can belong to other members, so
                          // the viewed user's id would over-constrain the
                          // dashboard filter. The reference_id alone pins the
                          // entry; only scope by user for the personal wallet.
                          userId:
                            wallet.wallet_kind === 'team' ? undefined : userId,
                          referenceId: entry.reference_id,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Axiom
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function UserCreditWalletSection({
  userId,
}: UserCreditWalletSectionProps) {
  const navigate = useNavigate({ from: '/users/$userId' });
  const routeSearch = useSearch({ from: '/users/$userId' });
  const page = routeSearch.walletPage;
  const sourceFilters = LEDGER_SOURCE_OPTIONS.filter((option) =>
    routeSearch.walletSources.includes(option.value)
  );
  const featureFilters = LEDGER_FEATURE_OPTIONS.filter((option) =>
    routeSearch.walletFeatures.includes(option.value)
  );
  const dateRange = routeSearch.walletDateRange;
  const minAmount = routeSearch.walletMinAmount;

  const since = dateRange
    ? new Date(Date.now() - dateRange * 86_400_000).toISOString()
    : null;

  const creditWalletQuery = useUserCreditWallet(userId, page, PAGE_SIZE, {
    sources: sourceFilters.map((option) => option.value),
    features: featureFilters.map((option) => option.value),
    since,
    minAmount: minAmount > 0 ? minAmount : null,
  });

  const handleSourceFiltersChange = (value: MultiValue<LedgerSourceOption>) => {
    navigate({
      search: {
        ...routeSearch,
        walletPage: 1,
        walletSources: value.map((option) => option.value),
      },
    });
  };

  const handleFeatureFiltersChange = (
    value: MultiValue<LedgerFeatureOption>
  ) => {
    navigate({
      search: {
        ...routeSearch,
        walletPage: 1,
        walletFeatures: value.map((option) => option.value),
      },
    });
  };
  const data = creditWalletQuery.data;
  const wallets = [data?.personal_wallet, data?.team_wallet].filter(
    (wallet): wallet is CreditWalletSummary =>
      wallet !== null && wallet !== undefined
  );
  const maxEntries = Math.max(
    0,
    ...wallets.map((wallet) => wallet.ledger_total_count)
  );
  const maxPage = Math.max(1, Math.ceil(maxEntries / PAGE_SIZE));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Wallet className="h-5 w-5" />
              Internal Usage Credits
            </CardTitle>
            <CardDescription>
              Wallet balances and ledger entries from CreditWallet.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {creditWalletQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Loading credits...</p>
        )}

        {creditWalletQuery.isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {creditWalletQuery.error instanceof Error
              ? creditWalletQuery.error.message
              : 'Failed to load credit wallet'}
          </div>
        )}

        {data && wallets.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No internal credit wallet exists for this user.
          </p>
        )}

        {data && wallets.length > 0 && (
          <>
            <div className="grid gap-4">
              {wallets.map((wallet) => (
                <WalletSummaryCard key={wallet.id} wallet={wallet} />
              ))}
            </div>

            {data.team_wallet && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Usage debits currently settle against the personal wallet.
              </div>
            )}

            <div className="space-y-5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ReceiptText className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Credit History</p>
                </div>
                <a
                  href={buildAxiomBillingUrl({ userId })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open user in Axiom
                </a>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <MultiSelectFilter<LedgerSourceOption>
                  options={LEDGER_SOURCE_OPTIONS}
                  value={sourceFilters}
                  onChange={handleSourceFiltersChange}
                  placeholder="All entry types"
                  label="Entry type"
                />
                <MultiSelectFilter<LedgerFeatureOption>
                  options={LEDGER_FEATURE_OPTIONS}
                  value={featureFilters}
                  onChange={handleFeatureFiltersChange}
                  placeholder="All features"
                  label="Feature"
                />
                <div className="flex min-w-40 flex-col gap-2">
                  <label className="text-xs font-semibold uppercase text-muted-foreground">
                    Date range
                  </label>
                  <Select
                    value={dateRange || ''}
                    onChange={(e) => {
                      navigate({
                        search: {
                          ...routeSearch,
                          walletPage: 1,
                          walletDateRange:
                            userEditSearchSchema.shape.walletDateRange.parse(
                              e.target.value
                            ),
                        },
                      });
                    }}
                  >
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex min-w-32 flex-col gap-2">
                  <label className="text-xs font-semibold uppercase text-muted-foreground">
                    Min amount
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={minAmount || ''}
                    placeholder="Any"
                    onChange={(e) => {
                      navigate({
                        search: {
                          ...routeSearch,
                          walletPage: 1,
                          walletMinAmount:
                            userEditSearchSchema.shape.walletMinAmount.parse(
                              e.target.value
                            ),
                        },
                        replace: true,
                      });
                    }}
                  />
                </div>
              </div>
              {data.personal_wallet && (
                <LedgerTable
                  title="Personal Wallet History"
                  wallet={data.personal_wallet}
                  userId={userId}
                />
              )}
              {data.team_wallet && (
                <LedgerTable
                  title="Team Wallet History"
                  wallet={data.team_wallet}
                  userId={userId}
                />
              )}
              {maxEntries > PAGE_SIZE && (
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1 || creditWalletQuery.isFetching}
                    onClick={() =>
                      navigate({
                        search: {
                          ...routeSearch,
                          walletPage: Math.max(1, page - 1),
                        },
                      })
                    }
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {maxPage}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= maxPage || creditWalletQuery.isFetching}
                    onClick={() =>
                      navigate({
                        search: {
                          ...routeSearch,
                          walletPage: Math.min(maxPage, page + 1),
                        },
                      })
                    }
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
